import axios from "axios";
import { apiUrl } from "@/env";
import {
  IUserProfile,
  ILandingOrder,
  ILandingOrderResponse,
  IOrder,
  ISignUp,
  IProductSentencesResponse,
  INewOrder,
} from "./interfaces";
import {
  IPackage,
  IInvoiceResponse,
  IPaymentResponse,
  IFinancesResponse,
  IFinanceFilter,
  IPackageStatistics,
  IPackageStatisticsResponse,
  IAvailablePromoPackage,
  IDexSummerPromo,
  IEmirexPaymentRequest,
  IEmirexPaymentData,
  IAvailableVirtualNode,
  IUserSigmaRings,
  ISigmaRing,
  IXcoinPackage,
  IUserXcoinPackage,
  IPhysicalNode,
  IVirtualNode,
  IAcquiredNode,
  INodeInvoice,
  INodeConfirm,
  IReferralConfirmation,
  IAcquiredPhysicalNode,
} from "./interfaces/mlm";

function authHeaders(token: string) {
  if (token) {
    return {
      headers: {
        Authorization: `JWT ${token}`,
      },
    };
  } else {
    return {};
  }
}

function authUploadHeaders(token: string) {
  return {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `JWT ${token}`,
    },
  };
}

export type FetchRequestInit = Omit<RequestInit, "method" | "body"> & {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: any;
};

export const api = {
  async authFetch(token: string, input: string, init: FetchRequestInit = {}) {
    const fullUrl = apiUrl + input;
    const headers = {
      ...init.headers,
      Authorization: `JWT ${token}`,
      "Content-Type": "application/json",
    };

    const method = init.method || "GET";

    const fetchOptions: RequestInit = {
      ...init,
      method,
      headers,
    };

    if (init.body && method !== "GET") {
      fetchOptions.body = JSON.stringify(init.body);
    }

    return fetch(fullUrl, fetchOptions);
  },
  async submitlogIn(data: {
    username: string;
    password: string;
    code: string;
    timezone_name?: any;
  }) {
    return axios.post<{ token: string; mfa_enabled?: boolean }>(
      `${apiUrl}/api/v1/accounts/login/`,
      data
    );
  },
  async mfaLogin(data: {
    username: string;
    password: string;
    code: string;
    timezone_name?: any;
    mfa_code: string;
  }) {
    return axios.post<{ token: string }>(
      `${apiUrl}/api/v1/accounts/mfa_login/`,
      data
    );
  },
  async logInByToken(authToken: string, language: string) {
    const params = new URLSearchParams();
    params.append("token", authToken);
    params.append("language", language);

    return axios.post(`${apiUrl}/api/v1/accounts/token-auth/`, params);
  },
  async submitSignUp(data: ISignUp) {
    return axios.post<{ token: string }>(
      `${apiUrl}/api/v1/accounts/register/`,
      data
    );
  },
  async checkPromocode(data: { referral: string }) {
    return axios.post<{ id: number }>(
      `${apiUrl}/api/v1/accounts/check_referral_id/`,
      data
    );
  },
  async submitRecoverPassword(data: { email: string }) {
    return axios.post(`${apiUrl}/accounts/email_password_reset/`, data);
  },
  async submitResetPassword(data: { token: string; password: string }) {
    return axios.post<{ status: string }>(
      `${apiUrl}/accounts/email_password_reset/confirm/`,
      data
    );
  },
  async submitSetLanguage(token: string, data: { language: string }) {
    return axios.post(
      `${apiUrl}/api/v1/accounts/set_language/`,
      data,
      authHeaders(token)
    );
  },
  async submitLandingOrder(data: ILandingOrder) {
    return axios.post<ILandingOrderResponse>(
      `${apiUrl}/api/v1/products/create_order_and_register_user/`,
      data
    );
  },
  async submitNewOrder(token: string, data: INewOrder) {
    return axios.post<IOrder>(
      `${apiUrl}/api/v1/products/create_order/`,
      data,
      authHeaders(token)
    );
  },
  // async getMe(token: string) {
  //   return axios.get<IUserProfile>(`${apiUrl}/api/v1/accounts/me/`, authHeaders(token));
  // },
  async getMe(token: string) {
    return axios.post<IUserProfile>(
      `${apiUrl}/api/v1/accounts/info/`,
      {},
      authHeaders(token)
    );
  },
  async updateUserInfo(token: string, data: { code_of_etics_status?: string }) {
    return axios.patch(
      `${apiUrl}/api/v1/accounts/info/`,
      data,
      authHeaders(token)
    );
  },
  async updateMe(token: string, data: any) {
    return axios.put<IUserProfile>(
      `${apiUrl}/api/v1/accounts/me/`,
      data,
      authHeaders(token)
    );
  },

  async requestUpdateMyEmail(token: string, data: any) {
    return axios.post(
        `${apiUrl}/api/v1/accounts/email_change/email/`,
        data,
        authHeaders(token)
    );
  },

  async confirmMyNewEmail(token: string, data: any) {
    return axios.get(`${apiUrl}/api/v1/accounts/email_change/email/confirm/?token=${data.token}&email=${data.email}`);
  },

  async changeEmailViaAuthApp(token: string, data: any){
    return axios.post(
        `${apiUrl}/api/v1/accounts/email_change/auth_app/`,
        data,
        authHeaders(token)
    );
  },

  async getUsdtDexnetRate(token: string, data: any){
    return axios.post(
        `${apiUrl}/api/v1/mlm/usdt_dexnet_rate/`,
        data,
        authHeaders(token)
    );
  },

  async createRequestDexNewYearPromo(token: string, data: any){
    return axios.post(
        `${apiUrl}/api/v1/mlm/dex_new_year_promo/`,
        data,
        authHeaders(token)
    );
  },

  async updateUserAvatar(token: string, data: FormData) {
    return axios.post<IUserProfile>(
      `${apiUrl}/api/v1/accounts/me/`,
      data,
      authUploadHeaders(token)
    );
  },
  async unsubscribeFromBot(token: string) {
    return axios.post(
      `${apiUrl}/api/v1/chatbot/send_unsubscribe_message/`,
      {},
      authHeaders(token)
    );
  },
  // 07.09.2026 проверка привязки ТГ
  /** {
  "current_user_id": 123,
  "telegram_group_user_ids": [123, 456, 789],
  "can_change_single": true,
  "can_change_group": true
} */
  async telegramConnectPreview(token: string) {
    return axios.get(
      `${apiUrl}/api/v1/chatbot/telegram-connect-intent/preview/`,
      authHeaders(token)
    );
  },
  async telegramConnectIntent(token: string, data: { scope: 'single' | 'group' }) {
    return axios.post(
      `${apiUrl}/api/v1/chatbot/telegram-connect-intent/`,
      data,
      authHeaders(token)
    )
  },
  async getPackagesList(token: string) {
    return axios.get<IPackage[]>(
      `${apiUrl}/api/v1/mlm/packages/`,
      authHeaders(token)
    );
  },
  async getPackagesTerms(token: string) {
    return axios.get(
      `${apiUrl}/api/v1/mlm/packages/terms/`,
      authHeaders(token)
    );
  },
  async getActivePackage(token: string) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/package/active/`,
      {},
      authHeaders(token)
    );
  },
  async getQualification(token: string) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/get_qualification/`,
      {},
      authHeaders(token)
    );
  },
  async makeProfixoneLink(token: string) {
    return axios.post<{ url: string }>(
      `${apiUrl}/api/v1/accounts/make_profixone_link/`,
      {},
      authHeaders(token)
    );
  },
  async getCompanyStats(token: string) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/company_stats/`,
      {},
      authHeaders(token)
    );
  },
  // async getOrdersList(token: string) {
  //   return axios.get<IOrder[]>(`${apiUrl}/api/v1/products/orders/`, authHeaders(token));
  // },
  async getOrCreateInvoice(
    token: string,
    data: { package: number; standart: string; token?: string }
  ) {
    return axios.post<IInvoiceResponse>(
      `${apiUrl}/api/v1/mlm/package/purchase/request/`,
      data,
      authHeaders(token)
    );
  },
  async processConfirmInvoice(token: string, data: { invoice: number }) {
    return axios.post<IInvoiceResponse>(
      `${apiUrl}/api/v1/mlm/package/purchase/confirm/`,
      data,
      authHeaders(token)
    );
  },
  // async getOrCreateInvoice(token: string, data: {package: number, standart: string, token?: string}) {
  //   return axios.post<IInvoiceResponse>(`${apiUrl}/api/v1/mlm/package/purchase/`, data, authHeaders(token));
  // },
  // async getOrCreateUpgradeInvoice(token: string, data: {package: number, standart: string, token?: string}) {
  //   return axios.post<IInvoiceResponse>(`${apiUrl}/api/v1/mlm/package/upgrade/`, data, authHeaders(token));
  // },
  async getOrCreateOrderInvoice(
    token: string,
    data: { order?: number | null; standart: string; token?: string }
  ) {
    return axios.post<IInvoiceResponse>(
      `${apiUrl}/api/v1/payments/get_or_create_invoice/`,
      data,
      authHeaders(token)
    );
  },
  async getPaymentStatus(
    token: string,
    data: { address: string; invoice: number }
  ) {
    return axios.post<IPaymentResponse>(
      `${apiUrl}/api/v1/payments/get_payment_status/`,
      data,
      authHeaders(token)
    );
  },
  async processPurchaseByUnit(token: string, data: { invoice: number }) {
    return axios.post(
      `${apiUrl}/api/v1/payments/process_purchase_by_unit/`,
      data,
      authHeaders(token)
    );
  },
  async getTransactionsList(
    token: string,
    data: IFinanceFilter,
    page: string
  ) {
    return axios.post<IFinancesResponse>(
        page ? page : `${apiUrl}/api/v1/payments/finances/`,
      data,
      authHeaders(token)
    );
  },
  async requestEmirexPayment(token: string, data: IEmirexPaymentRequest) {
    return axios.post<IEmirexPaymentData>(
      `${apiUrl}/api/v1/cardpayment/test_endpoint_carpayment/`,
      data,
      authHeaders(token)
    );
  },
  async getEmirexEuroRate(token: string) {
    return axios.post<{ usdt_euro_rate: number }>(
      `${apiUrl}/api/v1/cardpayment/usdt_euro_rate/`,
      {},
      authHeaders(token)
    );
  },
  async validateDexnetAddress(token: string, address: string) {
    return axios.post(
      `${apiUrl}/api/v1/withdraw/withdraw_address_dexnet_validation/`,
      { address: address },
      authHeaders(token)
    );
  },
  async checkDexfreedomTokenInfo(token: string) {
    return axios.post(
      `${apiUrl}/api/v1/withdraw/dft_to_send_to_dexnet/`,
      {},
      authHeaders(token)
    );
  },
  async submitWithdrawRequest(
    token: string,
    data: {
      amount: number;
      standart: string;
      token: string;
      wallet_address: string;
      security_code: string;
    }
  ) {
    return axios.post(
      `${apiUrl}/api/v1/withdraw/request/`,
      data,
      authHeaders(token)
    );
  },
  async getProductSentencesSet(
    token: string,
    data: { product_sentences_set: string; promocode?: string }
  ) {
    return axios.post<IProductSentencesResponse>(
      `${apiUrl}/api/v1/products/products/product_sentences_set/`,
      data,
      authHeaders(token)
    );
  },
  async getUserBinaryObject(token: string, data: { user: number }) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/get-binary-object/`,
      data,
      authHeaders(token)
    );
  },
  async getUserBinaryTree(token: string, data: { user: number }) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/get-binary-tree/`,
      data,
      authHeaders(token)
    );
  },
  async getUserRegistrationsTable(
    token: string,
    data: { user: number },
    rowQuery
  ) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/get-registrations-tables/${rowQuery}`,
      data,
      authHeaders(token)
    );
  },
  async getUserStructureList(token: string) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/structure_list/`,
      {},
      authHeaders(token)
    );
  },
  async getUserStructureSearch(token: string,data: { search: string }) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/structure_search/`,
       data,
      authHeaders(token)
    );
  },
  async setUserBinaryStructureSide(token: string, data: { side: string }) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/set-binary-structure-side/`,
      data,
      authHeaders(token)
    );
  },
  async getPurchaseStatisticsByPackages(token: string, data: any) {
    return axios.post<IPackageStatisticsResponse>(
      `${apiUrl}/api/v1/mlm/get_purchase_statistics_by_packages/`,
      data,
      authHeaders(token)
    );
  },
  async getAllConfigs(token: string) {
    return axios.get(
      `${apiUrl}/api/v1/configs/get_all_configs/`,
      authHeaders(token)
    );
  },
  async submitUpdateOrder(
    token: string,
    data: {
      order: number;
      delivery_address?: string;
      starlink_kit?: boolean;
      promocode?: string;
    }
  ) {
    return axios.post<IProductSentencesResponse>(
      `${apiUrl}/api/v1/products/update_order/`,
      data,
      authHeaders(token)
    );
  },
  async getUsers(token: string) {
    return axios.get<IUserProfile[]>(
      `${apiUrl}/api/v1/users/`,
      authHeaders(token)
    );
  },
  async passwordRecovery(email: string) {
    return axios.post(`${apiUrl}/api/v1/password-recovery/${email}`);
  },
  async resetPassword(password: string, token: string) {
    return axios.post(`${apiUrl}/api/v1/reset-password/`, {
      new_password: password,
      token,
    });
  },
  async getAvailablePromoPackages(token: string) {
    return axios.get<IAvailablePromoPackage[]>(
      `${apiUrl}/api/v1/promotions/promocodes/`,
      authHeaders(token)
    );
  },
  async submitActivatePromocode(token: string, data: { promocode: string }) {
    return axios.post<{ success: boolean }>(
      `${apiUrl}/api/v1/promotions/promocode/activate/`,
      data,
      authHeaders(token)
    );
  },
  async submitCreatePromocode(
    token: string,
    data: { promocode: number; standart: string; token: string }
  ) {
    return axios.post<{ promocode: string }>(
      `${apiUrl}/api/v1/promotions/promocode/purchase/request/`,
      data,
      authHeaders(token)
    );
  },
  async getVirtualNodeAvailableForUpgrade(token: string, promocode: string) {
    return axios.get<IAvailableVirtualNode[]>(
      `${apiUrl}/api/v1/mlm/virtual_node/available-for-upgrade/?promocode=${promocode}`,
      authHeaders(token)
    );
  },
  async getSigmaRings(token: string) {
    return axios.get<ISigmaRing[]>(
      `${apiUrl}/api/v1/mlm/sigma_rings/`,
      authHeaders(token)
    );
  },
  async getUserSigmaRings(token: string) {
    return axios.get<IUserSigmaRings[]>(
      `${apiUrl}/api/v1/mlm/user_sigma_rings`,
      authHeaders(token)
    );
  },
  async submitUpgradeVirtualNode(
    token: string,
    data: { promocode: number; virtual_node: number; standart: string; token: string }
  ) {
    /* Формирование инвойса для апгрейда виртуальной ноды
      virtual_node - ID виртуальной ноды, на которую делается апгрейд 
      token - Валюта оплаты: USDT или DEXNET
    */
    return axios.post<{ promocode: string }>(
      `${apiUrl}/api/v1/virtual_node/upgrade/request/`,
      data,
      authHeaders(token)
    );
  },
  async getFirstLineUsers(
    token: string,
    data: {
      user: number;
      search?: string;
      users_count_first_line_min?: number;
      users_count_first_line_max?: number;
      level_min?: number;
      level_max?: number;
    },
    rowQuery: string
  ) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/first-line/${rowQuery}`,
      data,
      authHeaders(token)
    );
  },
  async getFirstLineUsersForUser(
    token: string,
    data: { user: number; product?: number | null },
    rowQuery: string
  ) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/first-line-for-user/${rowQuery}`,
      data,
      authHeaders(token)
    );
  },
  // 25.02.2026
  async getUserPurchases(
    token: string,
    data: { user_id: number; }
  ) {
    // console.log('data ', data)
    return axios.post(
      `${apiUrl}/api/v1/mlm/user-purchases/`,
      data,
      authHeaders(token)
    );
  },
  async getReferralStructureInfo(token: string, data: any) {
    return axios.post(
      `${apiUrl}/api/v1/mlm/referral_structure_info/`,
      data,
      authHeaders(token)
    );
  },
  async getDexSummerPromoStatus(token: string, data: any) {
    return axios.post<IDexSummerPromo>(
      `${apiUrl}/api/v1/mlm/get_dex_summer_promo_status/`,
      data,
      authHeaders(token)
    );
  },
  // --- resident agreement --- //
  async submitResidentAgreement(token: string, data: any) {
    return axios.post(
      `${apiUrl}/api/v1/accounts/request_partner_agreement/`,
      data,
      authHeaders(token)
    );
  },
  async updateResidentAgreement(token: string, data: any) {
    return axios.post(
      `${apiUrl}/api/v1/accounts/update_partner_agreement_request/`,
      data,
      authHeaders(token)
    );
  },
  async getResidentAgreement(token: string) {
    return axios.get(
      `${apiUrl}/api/v1/accounts/last_partner_agreement_request/`,
      authHeaders(token)
    );
  },

  async createUserPromocode(token: string, data: any) {
    /**
   * 1) Эндпоинт для создания публичного промокода
    code - кастомное название промокода (пользователь может сгенерировать промокоди или назвать его самостоятельно), не обязательно
    comment - комментарий для промокода, не обязательно
    service_type - сервис, к которому относится промокод ("DEX_CLOUD", "DEX_PN", "DEX_MOBILE", "DEX_CARDS", "DEX_SAFE",null), не обязательно
    POST api/v1/dexnet_integration/create-dexid-promocode/
    {
    "code": "QwqruQ",
    "comment": "Comment",
    "service_type": "DEX_CLOUD"
    }
   */
    return axios.post(
      `${apiUrl}/api/v1/dexnet_integration/create-dexid-promocode/`,
      data,
      authHeaders(token)
    );
  },
  // --- dex id promocodes --- //
  
async getUserPromocodes(token: string) {
   /**
   * Эндпоинт для получения информации о промокодах пользователя
    dexid_promocode - промокод
    enabled - активный или нет
    dexid_promocode_type - тип промокода ("public", "private")
    service_type - сервис, к которому относится промокод ("DEX_CLOUD", "DEX_PN", "DEX_MOBILE", "DEX_CARDS", "DEX_SAFE")
    comment - комментарий для промокода
    count_used - кол-во использований промокода
    bonuses_by_currency - суммы бонусов по валютам
    GET api/v1/dexnet_integration/dexid-promocodes-info/
    {
    "dexid_promocode": "QwqruQ",
    "enabled": true,
    "dexid_promocode_type": "public",
    "service_type": "DEX_CLOUD",
    "comment": "Comment",
    "count_used": 1,
    "bonuses_by_currency": 
    {
    "UNT": 120,
    "X": 240,
    },
    }
   */
    return axios.get(
      `${apiUrl}/api/v1/dexnet_integration/dexid-promocodes-info`,
      authHeaders(token)
    );
  },
    
  async updateUserPromocode(token: string, data: any) {
      /**
     * Эндпоинт для обновления промокода
      promocode - обязательно
      POST api/v1/dexnet_integration/update-dexid-promocode/
      {
      "dexid_promocode": "QwqruQ",
      "comment": "Comment",
      "service_type": "DEX_CLOUD"
      }
     */
    return axios.post(
      `${apiUrl}/api/v1/dexnet_integration/update-dexid-promocode/`,
      data,
      authHeaders(token)
    );
  },

  async validateDexIdPromocode(token: string, data: any) {
      /**
     * Эндпоинт для проверки промокода
      code - обязательно
      POST /api/v1/dexnet_integration/validate-dexid-promocode/
      {
        "code": "YDKS34D"
      }
     */
    return axios.post(
      `${apiUrl}/api/v1/dexnet_integration/validate-dexid-promocode/`,
      data,
      authHeaders(token)
    );
  },
  // Доступные пакеты токенов -- 13.04.2026
  async getXcoinPackagesList(token: string) {
    return axios.get<IXcoinPackage[]>(
      `${apiUrl}/api/v2/token_service_integration/token_packages/`,
      authHeaders(token)
    );
  },
  // Пакеты токенов пользователя
  async getUserXcoinPackagesList(token: string) {
    return axios.get<IUserXcoinPackage[]>(
      `${apiUrl}/api/v2/token_service_integration/user_token_packages/`,
      authHeaders(token)
    );
  },
  async getActiveXcoinPackages(token: string) {
    return axios.post(
      `${apiUrl}/api/v1/token_service_integration/token_package/info/`,
      {},
      authHeaders(token)
    );
  },
  // 27.04.2026 new physical node
  async getPhysicalNodesList(token: string) {
    return axios.get<IPhysicalNode[]>(
      `${apiUrl}/api/v1/mlm/physical_nodes/`,
      authHeaders(token)
    );
  },
  async getPhysicalNodesActive(token: string) {
    return axios.get<IAcquiredPhysicalNode[]>(
      `${apiUrl}/api/v1/mlm/physical_node/active/`,
      authHeaders(token)
    );
  },
  // virt nodes 
  async getVirtualNodesList(token: string) {
    return axios.get<IVirtualNode[]>(
      `${apiUrl}/api/v1/mlm/virtual_nodes/`,
      authHeaders(token)
    );
  },
  async getVirtualNodesActive(token: string) {
    return axios.get<IAcquiredNode[]>(
      `${apiUrl}/api/v1/mlm/virtual_node/active/`,
      authHeaders(token)
    );
  },
  // Запрос на покупку physical node (создание invoice)
  async postPhysicalNodePurchaseRequest(
    token: string, 
    data: {
      physical_node: number,
      standart: string,
      token: string
    }){
    return axios.post<INodeInvoice>(
      `${apiUrl}/api/v1/mlm/physical_node/purchase/request/`,
      data,
      authHeaders(token)
    );
  },
  // подтверждение
  async physicalNodeConfirmInvoice(token: string, data: { invoice: number,period: number,
  email: string | undefined }) {
    return axios.post<INodeConfirm>(
      `${apiUrl}/api/v1/mlm/physical_node/purchase/confirm/`,
      data,
      authHeaders(token)
    );
  },
  async getReferralConfirmation(token: string) {
    return axios.get<IReferralConfirmation>(
      `${apiUrl}/api/v1/mlm/referral_confirmation/`,
      authHeaders(token)
    );
  },
  /* Возвращает месячные обороты текущего авторизованного пользователя. 29/05/2026
  Query params: period: YYYY-MM
  GET /api/v1/mlm/monthly_turnover_metrics/?period=2026-05 or POST
  {
  "period": "2026-05"
}
  */
  async getMonthlyTurnover(token: string) {
    return axios.get(
      `${apiUrl}/api/v1/mlm/monthly_turnover_metrics/`,
      authHeaders(token)
    );
  },
  async postTelegramRebind(token: string) {
    return axios.post(`${apiUrl}/api/v1/chatbot/telegram-rebind/start/`,{},
      authHeaders(token))
  }
};